import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import {
  Package, ClipboardCheck, Users, ArrowRight, CheckCircle2, Sparkles,
} from 'lucide-react';

/**
 * OnboardingPage — extremely simple post-signup landing.
 *
 * Shown after a successful Test Drive signup. Gives the new admin three
 * obvious "first action" cards and a "Skip to dashboard" escape hatch.
 * Marketing copy reinforces the Free plan promise (up to 10 assets).
 */
const STEPS = [
  {
    id: 'add-asset',
    icon: Package,
    title: 'Add your first asset',
    body: 'Crane, excavator, sling — anything you maintain. We pre-seeded a few samples to show how it works.',
    cta: 'Add an asset',
    href: '/assets?onboarding=1',
  },
  {
    id: 'run-inspection',
    icon: ClipboardCheck,
    title: 'Run an inspection',
    body: 'Mobile-friendly checklists with signature capture. Demo templates are already in place.',
    cta: 'Browse inspections',
    href: '/inspections',
  },
  {
    id: 'invite-team',
    icon: Users,
    title: 'Invite your team',
    body: 'Field & mobile users are always free. Add operators, mechanics and supervisors in one click.',
    cta: 'Invite users',
    href: '/users',
  },
];

const OnboardingPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading } = useAuth();
  const [done, setDone] = useState({});

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [loading, isAuthenticated, navigate]);

  if (loading || !isAuthenticated) {
    return null;
  }

  const firstName = (user?.name || '').split(' ')[0] || 'there';

  const handleStep = (step) => {
    setDone((d) => ({ ...d, [step.id]: true }));
    navigate(step.href);
  };

  return (
    <div className="min-h-screen bg-[#0e0e0e] text-white" data-testid="onboarding-page">
      <header className="border-b border-white/5 bg-[#121212]">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <h1 className="text-base font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
            EQUIPR <span className="text-[#FFD108]">Guardian</span>
          </h1>
          <button
            onClick={() => navigate('/dashboard')}
            className="text-xs text-muted-foreground hover:text-white transition-colors"
            data-testid="onboarding-skip-top"
          >
            Skip for now →
          </button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-12 space-y-10">
        {/* Welcome */}
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-[#FFD108] flex items-center justify-center shrink-0">
            <Sparkles className="w-6 h-6 text-black" />
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-[0.25em] text-[#FFD108] font-semibold mb-1">
              Welcome
            </p>
            <h2
              className="text-3xl md:text-4xl font-bold leading-tight"
              style={{ fontFamily: 'Manrope, sans-serif' }}
              data-testid="onboarding-welcome"
            >
              You're in, {firstName}.
            </h2>
            <p className="text-muted-foreground mt-3 max-w-2xl">
              Your workspace is ready and pre-loaded with sample data. Pick any of the three
              quick starts below — or jump straight into the dashboard and explore. You're on
              the <span className="text-[#FFD108] font-semibold">Free plan</span>, which stays
              free up to <span className="text-white font-semibold">10 assets</span>.
            </p>
          </div>
        </div>

        {/* Steps */}
        <div className="grid md:grid-cols-3 gap-4">
          {STEPS.map((step) => {
            const Icon = step.icon;
            const isDone = !!done[step.id];
            return (
              <div
                key={step.id}
                className="rounded-xl border border-white/5 bg-[#121212] p-6 flex flex-col"
                data-testid={`onboarding-card-${step.id}`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="w-10 h-10 rounded-lg bg-[#FFD108]/10 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-[#FFD108]" />
                  </div>
                  {isDone && <CheckCircle2 className="w-5 h-5 text-green-400" />}
                </div>
                <h3 className="text-base font-bold mb-1">{step.title}</h3>
                <p className="text-sm text-muted-foreground flex-1 mb-5">{step.body}</p>
                <button
                  onClick={() => handleStep(step)}
                  className="inline-flex items-center justify-center gap-1.5 h-9 px-3 rounded-lg border border-white/10 hover:border-[#FFD108]/40 hover:bg-[#FFD108]/[0.04] text-sm font-medium transition-colors"
                  data-testid={`onboarding-cta-${step.id}`}
                >
                  {step.cta}
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            );
          })}
        </div>

        {/* Skip footer */}
        <div className="pt-4 border-t border-white/5 flex items-center justify-between">
          <p className="text-xs text-muted-foreground">
            You can always come back to these from the dashboard.
          </p>
          <Button
            onClick={() => navigate('/dashboard')}
            className="btn-volt"
            data-testid="onboarding-go-dashboard"
          >
            Go to my dashboard
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </main>
    </div>
  );
};

export default OnboardingPage;
